import { useState, useEffect } from "react";
import { getUserRole } from "../../../lib/api/client";
import { AdminStatsTab } from "./AdminStatsTab";
import { AdminUsersTab } from "./AdminUsersTab";
import { AdminWaitlistTab } from "./AdminWaitlistTab";
import { AdminActivityTab } from "./AdminActivityTab";
import { AdminSystemTab } from "./AdminSystemTab";
import { AdminBugReportsTab } from "./AdminBugReportsTab";
import { AdminRoadmapTab } from "./AdminRoadmapTab";

interface AdminPanelProps {
  readonly onClose: () => void;
}

type Tab = "stats" | "users" | "activity" | "bugs" | "roadmap" | "waitlist" | "system";

const TAB_LABELS: Record<Tab, string> = {
  stats: "Stats",
  users: "Utilisateurs",
  activity: "Activité",
  bugs: "Bugs",
  roadmap: "Roadmap",
  waitlist: "Waitlist",
  system: "Système",
};

export function AdminPanel({ onClose }: AdminPanelProps) {
  const [tab, setTab] = useState<Tab>("stats");
  const [isMobile, setIsMobile] = useState(false);
  const isAdmin = getUserRole() === "ADMIN";
  const availableTabs: Tab[] = isAdmin
    ? ["stats", "users", "activity", "bugs", "roadmap", "waitlist", "system"]
    : ["stats"];

  // Détecter mobile via breakpoint
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`fixed inset-0 z-50 ${isMobile ? "flex flex-col" : "flex items-center justify-center"} p-0 md:p-4 pointer-events-none`}>
        <div className={`pointer-events-auto animate-scale-in bg-surface ${isMobile ? "w-full h-full rounded-none" : "rounded-2xl"} border border-border shadow-xl max-w-4xl w-full max-h-[90vh] ${isMobile ? "max-h-full" : ""} overflow-hidden flex flex-col`}>
          {/* Header - masqué sur mobile, les onglets sont en bas */}
          {!isMobile && (
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-4 overflow-x-auto">
                <h2 className="text-lg font-bold text-ink shrink-0">Administration</h2>
                {availableTabs.length > 1 && (
                  <div className="flex gap-1">
                    {availableTabs.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap min-h-[44px] touch-feedback ${
                          tab === t
                            ? "bg-accent text-white"
                            : "text-ink-muted hover:bg-surface-alt"
                        }`}
                      >
                        {TAB_LABELS[t]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-surface-alt text-ink-muted shrink-0 touch-feedback"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Mobile Header - titre et bouton close */}
          {isMobile && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <h2 className="text-base font-bold text-ink">Administration</h2>
              <button
                type="button"
                onClick={onClose}
                className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-surface-alt text-ink-muted shrink-0 touch-feedback"
                aria-label="Fermer"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Content - scrollable */}
          <div className="flex-1 overflow-y-auto p-0 md:p-4">
            {tab === "stats" && <AdminStatsTab />}
            {tab === "users" && isAdmin && <AdminUsersTab />}
            {tab === "activity" && isAdmin && <AdminActivityTab />}
            {tab === "bugs" && isAdmin && <AdminBugReportsTab />}
            {tab === "roadmap" && isAdmin && <AdminRoadmapTab />}
            {tab === "waitlist" && isAdmin && <AdminWaitlistTab />}
            {tab === "system" && isAdmin && <AdminSystemTab />}
          </div>

          {/* Mobile Bottom Tabs Navigation */}
          {isMobile && availableTabs.length > 1 && (
            <nav className="shrink-0 border-t border-border bg-surface-safe pb-[env(safe-area-inset-bottom)]" aria-label="Onglets d'administration">
              <div className="flex overflow-x-auto scrollbar-hide -mx-1 px-1">
                {availableTabs.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`flex-1 min-w-[70px] max-w-[100px] flex flex-col items-center justify-center gap-1 py-2 px-2 transition-colors min-h-[56px] touch-feedback ${
                      tab === t ? "text-accent bg-accent/5" : "text-ink-muted hover:text-ink hover:bg-surface-alt"
                    }`}
                    aria-label={TAB_LABELS[t]}
                    aria-current={tab === t ? "page" : undefined}
                  >
                    <svg className={`w-6 h-6 ${tab === t ? "fill-accent" : ""}`} viewBox="0 0 24 24">
                      {t === "stats" && <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>}
                      {t === "users" && <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>}
                      {t === "activity" && <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>}
                      {t === "bugs" && <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>}
                      {t === "roadmap" && <path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z"/>}
                      {t === "waitlist" && <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>}
                      {t === "system" && <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L3.16 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>}
                    </svg>
                    <span className="text-[10px] font-medium leading-tight truncate w-full text-center">
                      {TAB_LABELS[t]}
                    </span>
                  </button>
                ))}
              </div>
            </nav>
          )}
        </div>
      </div>
    </>
  );
}
